/** Pip, the living shopping bag: 12 fps reaction clips over an idle/worried base loop. */
import * as Phaser from 'phaser';
import type { Art } from '../game/art.js';
import { deco } from '../fx/motion.js';
import { outfitGroup } from '../services/cityOutfit.js';
import { dropLooks, loadLook, pipAnimKey } from './pip-looks.js';

type Rig = Pick<typeof import('@mp/art'), 'pipCostumeTransform' | 'pipCostumeOrigin'>;

export type PipAction = 'idle' | 'catch' | 'pop' | 'worried' | 'panic' | 'win' | 'slump' | 'celebrate' | 'wave' | 'sleep';

const PRIORITY: Record<PipAction, number> = {
  idle: 0,
  worried: 0,
  sleep: 0,
  wave: 1,
  catch: 1,
  pop: 2,
  celebrate: 3,
  slump: 4,
  panic: 5,
  win: 5,
};

const STAND_IN: Partial<Record<PipAction, PipAction>> = { worried: 'idle', slump: 'panic', celebrate: 'win', wave: 'idle', sleep: 'idle' };

export class PipView {
  readonly sprite: Phaser.GameObjects.Sprite;
  private base: PipAction = 'idle';
  private current: PipAction = 'idle';
  private locked = false;
  /** MT-09 costume overlay (chr_pip_cos_*), placed per frame with @mp/art's rig */
  private costume: Phaser.GameObjects.Image | null = null;
  private rig: Rig | null = null;
  /** the costume asked for last (the overlay may still be loading) */
  private worn = 'classic';
  /** the costume whose overlay is on screen now (null: none, or its sheet is still loading) */
  private drawn: string | null = null;
  private hooked = false;
  /** the look asked for last (views/pip-looks.ts may still be drawing it) and the one playing */
  private skin = 'classic';
  private shown = 'classic';

  constructor(
    private scene: Phaser.Scene,
    x: number,
    y: number,
    height: number,
  ) {
    this.sprite = scene.add.sprite(x, y, '__DEFAULT').setOrigin(0.5, 0.93).setDepth(3100);
    this.sprite.play('pip_idle');
    const k = height / this.sprite.height;
    this.sprite.setScale(k);
    this.sprite.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      if (!this.locked) this.play(this.base, true);
    });
  }

  /** The costume asked for last (`classic`, a wardrobe costume or a city outfit `city_<city>`); the overlay may still be loading. */
  get wearing(): string {
    return this.worn;
  }

  /** The costume whose overlay is on screen now: `wearing` once its sheet is in, null for classic or while it loads (test hooks). */
  get dressed(): string | null {
    return this.drawn;
  }

  /**
   * Wear a costume: the equipped one, or the city's outfit (services/cityOutfit.ts `outfitFor`).
   * Its overlay follows every animation frame through `pipCostumeTransform` (anchor, rotation,
   * squash). The rig and sheet load lazily (a wardrobe costume from `pip_extra`, a city outfit
   * from its pack's `costume_<city>`), so classic Pip costs nothing. Calling it again with
   * another costume swaps the overlay (the map re-dresses Pip when the profile or the store
   * equips one, or City outfits is switched).
   */
  async wear(id: string, art: Art): Promise<void> {
    if (id === this.worn) return;
    this.worn = id;
    if (id === 'classic') {
      this.costume?.destroy();
      this.costume = null;
      this.drawn = null;
      return;
    }
    const [rig] = await Promise.all([import('@mp/art'), art.ensureGroup(outfitGroup(id))]);
    const key = `chr_pip_cos_${id}`;
    if (this.worn !== id || !this.sprite.scene) return;
    // no overlay to show (its sheet failed): Pip goes classic rather than keep the one before, and the ask is
    // forgotten, as look() does, so the next call (the map re-dresses Pip on every store update) tries again
    if (!art.has(key)) {
      this.costume?.destroy();
      this.costume = null;
      this.drawn = null;
      this.worn = 'classic';
      return;
    }
    const f = art.get(key);
    this.rig = rig;
    this.drawn = id;
    if (this.costume) this.costume.setTexture(f.texture, f.frame);
    else this.costume = this.scene.add.image(0, 0, f.texture, f.frame).setOrigin(rig.pipCostumeOrigin.x, rig.pipCostumeOrigin.y).setDepth(this.sprite.depth + 1);
    if (!this.hooked) {
      this.hooked = true;
      this.sprite.on(Phaser.Animations.Events.ANIMATION_UPDATE, () => this.syncCostume());
      this.sprite.on(Phaser.Animations.Events.ANIMATION_START, () => this.syncCostume());
    }
    this.syncCostume();
  }

  /**
   * Wear a look (economy.skins). Its animations are drawn on the device the first time
   * (views/pip-looks.ts); Pip stays kraft until they are all in, then changes on the frame he
   * is at. The map and play scenes call it as they call `wear`.
   */
  async look(skin: string): Promise<void> {
    if (skin === this.skin) return;
    this.skin = skin;
    const ok = await loadLook(this.scene.game, skin);
    if (this.skin !== skin || !this.sprite.scene) return;
    this.shown = ok ? skin : 'classic';
    // its art failed to draw: forget the ask, so the next call (the map re-dresses Pip on every store update) tries again
    if (!ok) this.skin = this.shown;
    // same action, same frames: carry on where the old look was (a stand-in restarts)
    const same = this.sprite.anims.currentAnim?.key.replace(/@.*$/, '') === `pip_${this.current}`;
    this.play(this.current, true, same ? (this.sprite.anims.currentFrame?.index ?? 1) - 1 : 0);
    dropLooks(this.scene.game, this.shown);
  }

  private syncCostume(): void {
    const img = this.costume;
    const anim = this.sprite.anims.currentAnim;
    const fr = this.sprite.anims.currentFrame;
    if (!img || !this.rig || !anim || !fr) return;
    const action = anim.key.replace(/^pip_|@.*$/g, '') as Parameters<Rig['pipCostumeTransform']>[0];
    const t = this.rig.pipCostumeTransform(action, Math.max(0, fr.index - 1));
    const k = this.sprite.scaleX;
    const w = this.sprite.frame.realWidth;
    const h = this.sprite.frame.realHeight;
    // frame space (256 px, untrimmed) → world, around the sprite's origin
    img.setPosition(this.sprite.x + (t.x * (w / 256) - this.sprite.originX * w) * k, this.sprite.y + (t.y * (h / 256) - this.sprite.originY * h) * k);
    img.setRotation(t.rotation).setScale(k * t.scaleX * (w / 256), k * t.scaleY * (h / 256));
  }

  /** Base loop: idle or worried (bag has 5–6 items). */
  setBase(action: 'idle' | 'worried'): void {
    if (this.base === action) return;
    this.base = action;
    if (!this.locked && PRIORITY[this.current] <= 1) this.play(action, true);
  }

  react(action: PipAction): void {
    if (this.locked) return;
    if (PRIORITY[action] < PRIORITY[this.current] && this.sprite.anims.isPlaying && this.current !== this.base) return;
    this.play(action, true);
  }

  /** Terminal states (win / panic) hold until reset. */
  hold(action: 'win' | 'panic' | 'celebrate' | 'slump'): void {
    this.locked = false;
    this.play(action, true);
    this.locked = true;
  }

  release(): void {
    this.locked = false;
    this.play(this.base, true);
  }

  private play(action: PipAction, force: boolean, startFrame = 0): void {
    this.current = action;
    let key = pipAnimKey(action, this.shown);
    // extra actions stream in after the first load: stand in with a core one meanwhile (a look has them all)
    if (!this.scene.anims.exists(key)) key = pipAnimKey(STAND_IN[action] ?? 'idle', this.shown);
    if (!this.scene.anims.exists(key)) return;
    const loopCheer = (action === 'win' || action === 'celebrate') && deco(1) > 0;
    const last = (this.scene.anims.get(key)?.frames.length ?? 1) - 1;
    this.sprite.play({ key, ...(loopCheer ? { repeat: -1 } : {}), ...(startFrame > 0 ? { startFrame: Math.min(startFrame, last) } : {}) }, !force);
  }
}
